import { isTemporalEventId } from './calendar.helpers';
import { isTemporalAutoScheduledEvent } from './scheduler.helpers';

/**
 * Lightweight shape for calendar rows that may be ephemeral previews (not yet saved or purely illustrative).
 */
export type CalendarEphemeralPreviewLike = {
  eventId?: string;
  isTemporalSuggested?: boolean;
  isReplanPreview?: boolean;
};

type TemporarySchedulerTimeEditableLike = CalendarEphemeralPreviewLike & { allDay?: boolean };

/**
 * Where ephemeral / preview rows on the calendar come from:
 *
 * - **Plan my day / Plan my week (AI scheduler)** — Recoil `aiScheduledActions` →
 *   `translateAIScheduledActionToTemporalCalendarEvent` / `useAIScheduler`; `eventId` contains
 *   `ai_scheduler_`.
 * - **Assistant chat** — `temporalSuggestedEvents` merged onto the grid (e.g. `useFormattedResultHandler`
 *   for CTA adjustments, `usePlanEntryHandler`, task suggestions via `transformSuggestedTasksToEvents`);
 *   RBC rows use `isTemporalSuggested` and `eventId` values under `temporal-` (see
 *   `TEMPORAL_EVENT_PREFIX` in data-access chat helpers).
 * - **Metrics previews** — `useMetricsActivitiesInCalendar` temporal activity / energy-boost rows
 *   (`activity_…`, `energy_boost_…` via `isTemporalAutoScheduledEvent`).
 * - **New event slot** — `eventId === 'creating'` while the user is placing an event.
 */
export const isCalendarEphemeralPreviewEvent = (event: CalendarEphemeralPreviewLike): boolean => {
  if (!event) {
    return false;
  }
  if (event.isReplanPreview === true) {
    return true;
  }
  if (event.eventId === 'creating') {
    return true;
  }
  if (event.isTemporalSuggested && isTemporalEventId(event.eventId)) {
    return true;
  }
  return isTemporalAutoScheduledEvent(event.eventId ?? '', ['aiScheduler', 'activity', 'energyBoost']);
};

/** Assistant chat rows rendered from `temporalSuggestedEvents` → `transformChatSuggesteBlockToRBCEvents`. */
export const isAssistantChatTemporalSuggestedCalendarEvent = (
  event: CalendarEphemeralPreviewLike
): boolean => {
  // Chat preview rows from useFormattedResultHandler are marked as temporal suggestions,
  // but some CTA schedule adjustments can keep original (non-temporal) event ids.
  if (event.isTemporalSuggested) {
    return true;
  }
  return isTemporalEventId(event.eventId);
};

/**
 * Plan-my-day/week and assistant-chat CTA previews: open {@link TemporaryEventMenu}
 * instead of the full edit sheet.
 */
export const isTemporaryEventMenuEvent = (event: CalendarEphemeralPreviewLike): boolean => {
  if (!event.eventId) {
    return false;
  }
  if (event.isReplanPreview === true) {
    return true;
  }
  if (isTemporalAutoScheduledEvent(event.eventId, ['aiScheduler'])) {
    return true;
  }
  return isAssistantChatTemporalSuggestedCalendarEvent(event);
};

/** Plan-my-day / plan-week AI scheduler previews: allow editing time from TemporaryEventMenu. */
export const isTemporaryEventMenuSchedulerTimeEditable = (
  event: TemporarySchedulerTimeEditableLike
): boolean => {
  if (!event.eventId || event.allDay) {
    return false;
  }
  if (event.isReplanPreview === true) {
    return true;
  }
  return isTemporalAutoScheduledEvent(event.eventId, ['aiScheduler']);
};

/** @deprecated Use isTemporaryEventMenuEvent */
export const isCalendarEphemeralPreviewReadOnlyDetailsEvent = isTemporaryEventMenuEvent;
